import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FC, useEffect, useState } from "react";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import { useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import CustomErrorMessage from "@/components/forms/inputsWithRef/customErrorMessage";
import { api } from "@/helper/api";
import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import styles from "@/components/forms/resetEmailForm.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

interface Props {
  email?: string;
  userId?: string;
}

const ResetEmailForm: FC<Props> = ({ email, userId }) => {
  const [currentEmail, setCurrentEmail] = useState<string>("");
  const { handleError, errorModal } = useErrorModal();
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { errors },
  } = useForm<any>({ mode: "onChange", shouldFocusError: false });
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField({
      fieldOrder: ["newEmail", "confirmedNewEmail"],
    });
  const newEmailRegistration = register("newEmail", {
    required: t("field_required"),
  });
  const confirmedNewEmailRegistration = register("confirmedNewEmail", {
    required: t("field_required"),
    validate: (value: any) => {
      if (value.toLowerCase() === getValues("newEmail").toLowerCase()) {
        return;
      }
      return t("emails_dont_match");
    },
  });
  const navigate = useNavigate();
  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
    setNotificationMessage("");
  };

  useEffect(() => {
    if (email && email !== "") {
      setCurrentEmail(email);
    }
  }, [email]);

  const onSubmit = (data: any) => {
    const result = { email: data.confirmedNewEmail };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    api.genericApiRequest({
      entity: "user",
      entityId: userId,
      method: "put",
      successHandler: (res: any) => {
        setNotificationMessage(t("successfully_saved"));
        setNotificationVariant("success");
        setShowNotification(true);
        navigate("/usersettings");
        setCurrentEmail(res.data.email);
        reset();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      submitData: result,
      config: config,
    });
  };

  return (
    <>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-label="Expand">
          {t("change_email")}
        </AccordionSummary>
        <AccordionDetails aria-label="Details">
          <form
            onSubmit={handleSubmit(onSubmit, (errors) => {
              focusFirstInvalidField(errors);
            })}
            className={styles.formStyle}
            noValidate
            aria-label="form"
          >
            <DefaultInput
              label={t("current_email")}
              value={currentEmail}
              type="text"
              inputClassName={styles.inputStyle}
              readonly={true}
              disabled={true}
            />
            <hr />
            <InputWithRef
              {...newEmailRegistration}
              ref={(element) => {
                newEmailRegistration.ref(element);
                registerValidationTarget("newEmail")(element);
              }}
              label={t("new_email")}
              className={styles.inputStyle}
            />
            <ErrorMessage
              errors={errors}
              name={"newEmail"}
              render={({ message }) => (
                <CustomErrorMessage error={"error"} errorMessage={message} />
              )}
            />

            <InputWithRef
              {...confirmedNewEmailRegistration}
              ref={(element) => {
                confirmedNewEmailRegistration.ref(element);
                registerValidationTarget("confirmedNewEmail")(element);
              }}
              label={t("confirm_new_email")}
              className={styles.inputStyle}
            />
            <ErrorMessage
              errors={errors}
              name={"confirmedNewEmail"}
              render={({ message }) => (
                <CustomErrorMessage error={"error"} errorMessage={message} />
              )}
            />

            <GenericButton className={styles.submitButton} type="submit">
              {t("save")}
            </GenericButton>
          </form>
        </AccordionDetails>
      </Accordion>
      {errorModal}
      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
};

export default ResetEmailForm;
